"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="205426" className="flex gap-6">
        <div className="h-4 w-16 bg-muted animate-pulse rounded" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href} className="relative inline-block py-2 text-[12px] uppercase tracking-widest font-default text-foreground transition-colors hover:text-primary after:content-[''] after:absolute after:w-0 after:h-[1px] after:bg-primary after:left-1/2 after:-translate-x-1/2 after:bottom-0 after:transition-all after:duration-300 hover:after:w-full after:shadow-[0_0_8px_var(--color-primary)]">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="p-6 space-y-4">
        <div className="h-4 w-24 bg-muted animate-pulse rounded" />
        <div className="h-4 w-32 bg-muted animate-pulse rounded" />
      </div>;
  }
  return <nav className="flex flex-col py-2">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href} onClick={onItemClick} className="block px-6 py-4 text-[12px] uppercase tracking-widest font-default text-foreground hover:bg-accent/20 hover:text-primary transition-colors border-b border-border/30 last:border-0">
            {item.label}
          </Link>
        </div>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`dark bg-background text-foreground sticky top-0 z-50 transition-all duration-300 ${scrolled ? "bg-background/80 backdrop-blur-xl border-b border-border/50 shadow-sm" : "bg-background border-b border-transparent"}`}>
      <header className="container mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
        {}
        <Link to="/" className="flex items-center z-50 relative">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1631141/img/luxmall_logo_vector.png" alt="AutoPulse" className="h-7 w-auto object-contain" />
        </Link>

        {}
        <MenuProvider menu_id="46">
          <DesktopMenuConsumer />
        </MenuProvider>

        {}
        <Button variant="ghost" size="icon" className="md:hidden z-50 relative text-foreground hover:bg-accent/20 hover:text-primary" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
          {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </Button>
      </header>

      {}
      <div className={`md:hidden absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-border/50 transition-all duration-300 ease-in-out overflow-hidden ${mobileMenuOpen ? "max-h-[70vh] opacity-100" : "max-h-0 opacity-0"}`}>
        <MenuProvider menu_id="46">
          <MobileMenuConsumer onItemClick={() => setMobileMenuOpen(false)} />
        </MenuProvider>
      </div>
    </section>;
}